import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Divider,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useEffect, useId, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { Switch } from '@/components/form/baseInputs/Switch';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';

import { useGrowthProfileModalContext } from '../GrowthProfileModal.context';
import { GrowthProfileModalFormShape } from '../GrowthProfileModal.types';
import { getTaxDragEntityDescription } from './TaxDrag.entityUtils';
import {
  TaxDragEntity,
  TaxDragEntityGroup,
  TaxDragOverrideFormShape,
} from './TaxDrag.types';
import { getDefaultTaxDragRate, groupTaxDragEntities } from './TaxDrag.utils';
import { TaxDragRateField } from './TaxDragRateField';

const TAX_DRAG_GROUP_ORDER = [
  TaxDragEntityGroup.InEstate,
  TaxDragEntityGroup.OutOfEstate,
  TaxDragEntityGroup.Draft,
];

const GrowthProfileCheckboxIcon = styled('span')({
  alignItems: 'center',
  backgroundColor: COLORS.PRIMITIVES.WHITE,
  border: `1px solid ${COLORS.GRAY[500]}`,
  borderRadius: 4,
  boxSizing: 'border-box',
  display: 'flex',
  height: 20,
  justifyContent: 'center',
  width: 20,
});

const GrowthProfileCheckboxSelectedIcon = styled(GrowthProfileCheckboxIcon)({
  backgroundColor: COLORS.GRAY[700],
  border: 0,
  color: COLORS.PRIMITIVES.WHITE,
});

interface EntityTaxDragOverridesEditorProps {
  entities: TaxDragEntity[];
  stateCode?: string | null;
}

export function EntityTaxDragOverridesEditor({
  entities,
  stateCode,
}: EntityTaxDragOverridesEditorProps) {
  const { disabled } = useFormFieldsDisabled();
  const { taxDragDefaults } = useGrowthProfileModalContext();
  const {
    control,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<GrowthProfileModalFormShape>();
  const overrides = useWatch({ control, name: 'taxDrag.overrides' });
  const [showEntityOverrides, setShowEntityOverrides] = useState(
    overrides.length > 0
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(TAX_DRAG_GROUP_ORDER);
  const contentId = useId();
  const groupedEntities = useMemo(
    () => groupTaxDragEntities(entities),
    [entities]
  );

  useEffect(() => {
    if (overrides.length > 0) setShowEntityOverrides(true);
  }, [overrides.length]);

  useEffect(() => {
    if (submitCount > 0 && errors.taxDrag?.overrides) {
      setIsExpanded(true);
      setExpandedGroups(TAX_DRAG_GROUP_ORDER);
    }
  }, [submitCount, errors.taxDrag?.overrides]);

  const setOverrides = (next: TaxDragOverrideFormShape[]) =>
    setValue('taxDrag.overrides', next, { shouldDirty: true });

  const toggleOverride = (entity: TaxDragEntity, enabled: boolean) => {
    if (!enabled) {
      setOverrides(overrides.filter(({ entityId }) => entityId !== entity.id));
      return;
    }
    setOverrides([
      ...overrides,
      {
        entityId: entity.id,
        ...getDefaultTaxDragRate(stateCode, taxDragDefaults),
      },
    ]);
  };

  return (
    <>
      <Divider />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1} sx={{ '& .MuiTypography-body-xs': { display: 'block' } }}>
          <Checkbox
            disabled={disabled}
            sx={{
              alignSelf: 'flex-start',
              '&:hover .GrowthProfileCheckbox-icon': {
                borderColor: COLORS.GRAY[800],
              },
              '&:hover .GrowthProfileCheckbox-iconSelected': {
                backgroundColor: COLORS.GRAY[800],
              },
              '&.Mui-disabled .GrowthProfileCheckbox-icon': {
                borderColor: COLORS.GRAY[300],
              },
              '&.Mui-disabled .GrowthProfileCheckbox-iconSelected': {
                backgroundColor: COLORS.GRAY[300],
              },
              '&.Mui-focusVisible': {
                borderRadius: 1,
                outlineOffset: 2,
              },
            }}
            size="medium"
            icon={
              <GrowthProfileCheckboxIcon className="GrowthProfileCheckbox-icon" />
            }
            checkedIcon={
              <GrowthProfileCheckboxSelectedIcon className="GrowthProfileCheckbox-icon GrowthProfileCheckbox-iconSelected">
                <CheckIcon aria-hidden size={16} />
              </GrowthProfileCheckboxSelectedIcon>
            }
            label="Customize tax drag per entity"
            helpText="Override the default tax drag rate for individual entities, on a one-off basis. All other entities will continue to follow either the asset category default set at the firm level or entity level override."
            value={showEntityOverrides}
            onChange={(event) => {
              setShowEntityOverrides(event.target.checked);
              if (event.target.checked) setIsExpanded(true);
              if (!event.target.checked) setOverrides([]);
            }}
          />
        </Box>
        <IconButton
          ariaLabel={
            isExpanded && showEntityOverrides
              ? 'Collapse entity tax drag overrides'
              : 'Expand entity tax drag overrides'
          }
          aria-expanded={isExpanded && showEntityOverrides}
          aria-controls={contentId}
          icon={ChevronDownIcon}
          iconProps={{ size: 24 }}
          size="xs"
          variant="transparent"
          disabled={!showEntityOverrides}
          onClick={() => setIsExpanded((expanded) => !expanded)}
          sx={{
            '& svg': {
              transform:
                isExpanded && showEntityOverrides
                  ? 'rotate(180deg)'
                  : undefined,
            },
          }}
        />
      </Stack>
      {showEntityOverrides && (
        <Collapse in={isExpanded} id={contentId}>
          <Stack spacing={2}>
            {TAX_DRAG_GROUP_ORDER.map((group) => {
              const groupEntities = groupedEntities[group];
              if (!groupEntities?.length) return null;
              return (
                <Accordion
                  key={group}
                  expanded={expandedGroups.includes(group)}
                  onChange={(_event, expanded) =>
                    setExpandedGroups((groups) =>
                      expanded
                        ? [...groups, group]
                        : groups.filter((item) => item !== group)
                    )
                  }
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: 'transparent',
                    '&&.MuiPaper-root': { m: 0, py: 0 },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDownIcon size={16} />}
                    sx={{
                      flexDirection: 'row-reverse',
                      gap: 1,
                      px: 0,
                      minHeight: 24,
                      borderBottom: `1px solid ${COLORS.GRAY[300]}`,
                      '& .MuiAccordionSummary-content': { my: 0.5 },
                    }}
                  >
                    <Typography
                      variant="eyebrow-xs"
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {group}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Stack
                      spacing={0}
                      sx={{
                        '& > :not(:last-child)': {
                          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
                        },
                      }}
                    >
                      {groupEntities.map((entity) => {
                        const overrideIndex = overrides.findIndex(
                          ({ entityId }) => entityId === entity.id
                        );
                        return (
                          <EntityTaxDragOverrideRow
                            key={entity.id}
                            entity={entity}
                            override={overrides[overrideIndex]}
                            overrideIndex={overrideIndex}
                            stateCode={stateCode}
                            onToggle={(enabled) =>
                              toggleOverride(entity, enabled)
                            }
                          />
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Collapse>
      )}
    </>
  );
}

interface EntityTaxDragOverrideRowProps {
  entity: TaxDragEntity;
  override?: TaxDragOverrideFormShape;
  overrideIndex: number;
  stateCode?: string | null;
  onToggle: (enabled: boolean) => void;
}

function EntityTaxDragOverrideRow({
  entity,
  override,
  overrideIndex,
  stateCode,
  onToggle,
}: EntityTaxDragOverrideRowProps) {
  const { disabled } = useFormFieldsDisabled();
  return (
    <Box sx={{ py: 1.5 }}>
      <Switch
        disabled={disabled}
        label={entity.displayName}
        labelPosition="right"
        helpText={getTaxDragEntityDescription(entity)}
        value={overrideIndex >= 0}
        onChange={(event) => onToggle(event.target.checked)}
      />
      {override && (
        <Box pt={1.5}>
          <TaxDragRateField
            label="Entity tax drag"
            rate={override}
            ratePath={`taxDrag.overrides.${overrideIndex}`}
            stateCode={stateCode}
          />
        </Box>
      )}
    </Box>
  );
}
